import { Component } from '@theme/component';

const normalize = (value) => String(value || '').trim().toLowerCase();
const packNumber = (value) => Math.max(1, Number(String(value || '').match(/\d+/)?.[0] || 1));
const intervalDays = (name) => {
  const value = packNumber(name);
  return value * (/week/i.test(name) ? 7 : /day/i.test(name) ? 1 : /year/i.test(name) ? 365 : 30);
};

class MonoProductInfo extends Component {
  #controller;
  #thumbObserver;
  #mainSwiper;
  #thumbSwiper;
  #reviewSwiper;
  #zoom;
  #labGallery;
  #modalTrigger;
  #busy = false;
  #initialized = false;
  #flavor;
  #pack;
  #planId;
  #config;
  #cards = [];
  #form;
  #main;
  #thumbs;
  #optimized = false;
  #badgeObserver;
  #layoutFrame;
  #galleryWidth;
  #faqRows = [];
  #guideController;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #initialize() {
    if (this.#controller) return;
    const config = this.querySelector('[data-mono-config]');
    if (!config) return;
    this.#config = JSON.parse(config.textContent);
    this.#optimized = this.hasAttribute('data-gummies-performance');
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#form = this.querySelector('form[action*="/cart/add"]');
    this.#main = this.querySelector('.product-main-images');
    this.#thumbs = this.querySelector('.product-thumb-images');
    this.#cards = [...this.querySelectorAll('[data-mono-pack]')];
    if (this.#optimized) this.#faqRows = [...this.querySelectorAll('.mono-clean-faq-item')].map(item => ({ item, content: item.querySelector('.faq-item__content') }));
    const flavors = this.#config.variants.map(variant => normalize(variant.flavor));
    const params = new URLSearchParams(location.search);
    const requestedFlavor = normalize(params.get('flavor'));
    const initialFlavor = this.querySelector('.product-images')?.dataset.initialFlavor;
    this.#flavor = flavors.includes(requestedFlavor) ? requestedFlavor : flavors.includes(initialFlavor) ? initialFlavor : flavors[0];
    this.#pack = this.#cards.find(card => packNumber(card.dataset.packLabel) === Number(params.get('pack_selected')))?.dataset.packLabel || this.#cards[0]?.dataset.packLabel || '1 Pack';
    this.#planId = null;
    this.#busy = false;
    this.addEventListener('click', this.handleClick, { signal });
    this.addEventListener('keydown', event => {
      if (!(event.target instanceof Element)) return;
      const thumbnail = event.target.closest('.product-thumb-images .swiper-slide');
      if (thumbnail && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        thumbnail.click();
      }
    }, { signal });
    this.#form?.addEventListener('submit', this.handleSubmit, { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.#guideController) this.#closeModal();
      const modal = this.querySelector('.guide-popup');
      if (modal?.style.display !== 'flex') return;
      if (event.key === 'Escape') this.#closeModal();
      if (event.key === 'Tab') {
        const controls = [...modal.querySelectorAll('a[href], button, [tabindex="0"]')];
        const first = controls[0], last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }, { signal });
    window.addEventListener('resize', () => {
      if (this.#optimized) {
        this.#scheduleLayout();
        return;
      }
      this.#refreshFaqHeights();
      this.#scaleBadge();
    }, { signal });
    this.#render();
    const rebuild = this.#initialized || this.querySelector('.product-images')?.dataset.initialFlavor !== this.#flavor;
    if (rebuild) this.#rebuildGallery();
    if (!this.#optimized || !rebuild) {
      this.#updateHero();
      this.#observeThumbnails();
    }
    this.#refreshFaqHeights();
    this.#initSwipers(signal);
    this.#initReviews(signal);
    if (this.#optimized && 'ResizeObserver' in window) {
      this.#galleryWidth = Number(this.querySelector('.product-images')?.dataset.galleryWidth) || undefined;
      this.#scaleBadge(this.#galleryWidth);
      this.#badgeObserver = new ResizeObserver(entries => {
        this.#galleryWidth = entries[0]?.contentRect.width;
        this.#scheduleLayout();
      });
      if (this.#main) this.#badgeObserver.observe(this.#main);
    } else this.#scaleBadge();
    this.#initialized = true;
  }

  #cleanup() {
    this.#controller?.abort();
    this.#controller = null;
    this.#thumbObserver?.disconnect();
    this.#badgeObserver?.disconnect();
    cancelAnimationFrame(this.#layoutFrame);
    this.#layoutFrame = undefined;
    this.#mainSwiper?.destroy(true, true);
    this.#thumbSwiper?.destroy(true, true);
    this.#reviewSwiper?.destroy(true, true);
    this.#mainSwiper = this.#thumbSwiper = this.#reviewSwiper = null;
    this.#zoom?.destroy();
    this.#labGallery?.destroy();
    this.#zoom = this.#labGallery = null;
    for (const video of this.querySelectorAll('video')) video.pause();
    this.#closeModal();
  }

  #variant(variants, pack = this.#pack) {
    return variants.find(variant => normalize(variant.flavor) === this.#flavor && normalize(variant.pack) === normalize(pack));
  }

  #allocation(variant, pack = this.#pack, preserve = true) {
    const allocations = [...(variant?.selling_plan_allocations || [])].sort((a, b) => intervalDays(a.selling_plan_name) - intervalDays(b.selling_plan_name));
    return (preserve && allocations.find(plan => String(plan.selling_plan_id) === String(this.#planId))) || allocations.find(plan => intervalDays(plan.selling_plan_name) === packNumber(pack) * 30) || allocations[0];
  }

  #cartState(subscription = true) {
    const display = this.#variant(this.#config.variants);
    const variant = this.#variant(this.#config.originalVariants);
    const displayPlan = this.#allocation(display);
    const allocations = variant?.selling_plan_allocations || [];
    const plan = allocations.find(allocation => normalize(allocation.selling_plan_name) === normalize(displayPlan?.selling_plan_name)) || allocations[0];
    const available = Boolean(variant?.available && (subscription ? displayPlan && plan : !variant.requires_selling_plan));
    return { variant, plan, available };
  }

  #money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency', currency: window.Shopify?.currency?.active || 'USD',
    }).format(Number(cents || 0) / 100);
  }

  #render() {
    const display = this.#variant(this.#config.variants);
    const allocation = this.#allocation(display);
    this.#planId = allocation?.selling_plan_id;
    for (const item of this.querySelectorAll('.product-flavor-item')) {
      const active = normalize(item.dataset.flavor) === this.#flavor;
      item.classList.toggle('active', active);
      item.setAttribute('aria-pressed', String(active));
    }
    for (const label of this.querySelectorAll('[data-selected-flavor]')) {
      label.textContent = this.querySelector('.product-flavor-item.active p')?.textContent || '';
    }
    for (const card of this.#cards) {
      const active = normalize(card.dataset.packLabel) === normalize(this.#pack);
      card.classList.toggle('is-active', active);
      const button = card.querySelector('[data-mono-pack-button]');
      button?.setAttribute('aria-pressed', String(active));
      const variant = this.#variant(this.#config.variants, card.dataset.packLabel);
      const original = this.#variant(this.#config.originalVariants, card.dataset.packLabel);
      const plan = this.#allocation(variant, card.dataset.packLabel, active);
      if (button) button.disabled = !original?.available || !plan || !original?.selling_plan_allocations?.length;
      if (plan) {
        card.querySelector('.mono-clean-offer__price').textContent = this.#money(plan.price);
        card.querySelector('[data-pack-per-day]').textContent = this.#config.labels.perDay.replace('[[price]]', this.#money(Math.round(plan.price / (packNumber(card.dataset.packLabel) * 30))));
      }
    }
    const { variant, plan, available } = this.#cartState();
    if (this.#form) {
      this.#form.elements.id.value = variant?.id || '';
      this.#form.elements.quantity.value = variant?.quantity || 1;
      const planInput = this.#form.elements.selling_plan;
      planInput.disabled = !plan;
      planInput.value = plan?.selling_plan_id || '';
      for (const property of this.#form.querySelectorAll('[name^="properties["]')) property.disabled = !plan;
    }
    const submit = this.querySelector('.product-info-btn');
    if (submit) submit.disabled = this.#busy || !available;
    const label = this.querySelector('[data-cart-label]');
    if (label) label.textContent = available ? this.#config.labels.add : this.#config.labels.soldOut;
    const otp = this.querySelector('[data-mono-otp]');
    if (otp) otp.disabled = !variant?.available || variant.requires_selling_plan || this.#busy;
    const otpPrice = this.querySelector('[data-mono-otp-price]');
    if (otpPrice) otpPrice.textContent = this.#money(variant?.price);
    const refill = this.querySelector('[data-mono-refill-frequency]');
    if (refill) refill.textContent = (packNumber(this.#pack) === 1 ? this.#config.labels.refill : this.#config.labels.refillPlural.replace('[[count]]', String(packNumber(this.#pack))));
  }

  /** @param {MouseEvent} event */
  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const otp = event.target.closest('[data-mono-otp]');
    if (otp) {
      event.preventDefault();
      if (!otp.disabled) this.#addToCart(false);
      return;
    }
    const flavor = event.target.closest('.product-flavor-item');
    if (flavor) {
      this.#flavor = normalize(flavor.dataset.flavor);
      this.#render();
      this.#rebuildGallery();
      if (!this.#optimized) this.#updateHero();
      this.#initSwipers(this.#controller.signal);
      return;
    }
    const pack = event.target.closest('[data-mono-pack-button]');
    if (pack) {
      this.#pack = pack.closest('[data-pack-label]').dataset.packLabel;
      this.#planId = null;
      this.#render();
      this.#updateHero();
      return;
    }
    const thumb = event.target.closest('.product-thumb-images .swiper-slide');
    if (thumb) {
      const index = [...thumb.parentElement.children].indexOf(thumb);
      this.#loadGalleryWindow(index);
      this.#mainSwiper?.slideTo(index);
      return;
    }
    const videoButton = event.target.closest('[data-play-video]');
    if (videoButton) {
      this.#activateVideo(videoButton.closest('[data-video-slide]'), true);
      return;
    }
    if (event.target.closest('.product-main-image:not([data-video-slide])')) {
      this.#openZoom(event.target.closest('.product-main-image'));
      return;
    }
    if (event.target.closest('.product-info-tested')) {
      if (typeof window.lightGallery !== 'function') return;
      const badge = this.querySelector('.product-info-tested');
      const galleries = JSON.parse(badge.dataset.testedGallery || '{}');
      const items = galleries[this.#flavor] || galleries.strawberry || [];
      this.#labGallery?.destroy();
      this.#labGallery = window.lightGallery(badge, { dynamic: true, dynamicEl: items, thumbnail: true, download: false });
      this.#labGallery.openGallery(0);
      return;
    }
    const trigger = event.target.closest('.faq-item__trigger');
    if (trigger) {
      event.stopPropagation();
      const item = trigger.closest('.mono-clean-faq-item');
      const opening = !item.classList.contains('faq-item--open');
      for (const other of this.querySelectorAll('.mono-clean-faq-item')) {
        const open = other === item && opening;
        other.classList.toggle('faq-item--open', open);
        other.querySelector('.faq-item__trigger')?.setAttribute('aria-expanded', String(open));
      }
      this.#refreshFaqHeights();
    }
    const modalButton = event.target.closest('[data-modal-trigger]');
    if (modalButton) {
      const modal = this.querySelector('.guide-popup');
      if (!modal || modal.id !== modalButton.dataset.modalId) return;
      this.#openGuide(modalButton, modal);
    }
    const modal = this.querySelector('.guide-popup');
    if (event.target === modal || event.target.closest('.close-popup')) this.#closeModal();
  };

  /** @param {SubmitEvent} event */
  handleSubmit = (event) => {
    event.preventDefault();
    return this.#addToCart(!event.submitter?.matches('[data-mono-otp]'));
  };

  async #addToCart(subscription) {
    if (this.#busy) return;
    const { variant, plan, available } = this.#cartState(subscription);
    if (!available) return;
    const controller = this.#controller;
    const fromSticky = Boolean(window.__sicomFromSticky);
    window.__sicomFromSticky = false;
    const properties = subscription ? {
      Subscription: plan.selling_plan_name,
      _gummies_welcome_kit: 'true',
      _gummies_mono_gifts: 'true',
    } : {};
    if (fromSticky) properties.Source = 'Sticky';
    this.#busy = true;
    this.#render();
    this.#form?.setAttribute('aria-busy', 'true');
    const status = this.querySelector('[data-cart-status]');
    if (status) status.textContent = '';
    try {
      if (typeof window.sicomAddToCart === 'function') {
        await window.sicomAddToCart(variant.id, variant.quantity || 1, subscription ? plan.selling_plan_id : null, properties);
      } else {
        const item = { id: variant.id, quantity: variant.quantity || 1, properties };
        if (subscription) item.selling_plan = plan.selling_plan_id;
        const response = await fetch(this.#config.cartAddUrl, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] }), signal: this.#controller.signal,
        });
        if (!response.ok) throw new Error('Cart add failed');
        location.assign(this.#config.cartUrl);
      }
    } catch (error) {
      if (!controller.signal.aborted && error.name !== 'AbortError' && status) status.textContent = this.#config.labels.error;
    } finally {
      if (this.#controller !== controller) return;
      this.#busy = false;
      if (this.isConnected) {
        this.#form?.removeAttribute('aria-busy');
        this.#render();
      }
    }
  }

  #activateImage(image, priority = 'auto', loading = 'eager') {
    if (!(image instanceof HTMLImageElement)) return;
    if (this.#optimized) {
      image.loading = loading;
      image.fetchPriority = priority;
    }
    if (image.hasAttribute('src')) return;
    if (!this.#optimized) {
      image.loading = 'eager';
      image.fetchPriority = priority;
    }
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    if (image.dataset.src) image.src = image.dataset.src;
  }

  #loadGalleryWindow(start = 0) {
    for (const gallery of [this.#main, this.#thumbs]) {
      const slides = [...(gallery?.querySelector('.swiper-wrapper')?.children || [])];
      for (const [offset, slide] of slides.slice(start, start + 5).entries()) {
        const index = start + offset;
        const isHero = gallery === this.#main && index === 0;
        this.#activateImage(slide.querySelector('img'), isHero ? 'high' : 'auto', this.#optimized && !isHero ? 'lazy' : 'eager');
      }
    }
  }

  #observeThumbnails() {
    this.#thumbObserver?.disconnect();
    if (!this.#thumbs) return;
    this.#thumbObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
        const image = entry.target.querySelector('img');
        this.#activateImage(image, 'auto', this.#optimized ? 'lazy' : 'eager');
      }
    }, { root: this.#thumbs, threshold: 0.5 });
    for (const slide of this.#thumbs.querySelectorAll('.swiper-slide')) this.#thumbObserver.observe(slide);
  }

  #rebuildGallery() {
    this.#mainSwiper?.destroy(true, true);
    this.#thumbSwiper?.destroy(true, true);
    this.#mainSwiper = this.#thumbSwiper = null;
    for (const video of this.querySelectorAll('.product-main-images video')) video.pause();
    const badge = this.#main?.querySelector('.frontrow-img-badge');
    if (badge) this.#main.prepend(badge);
    for (const [templateName, gallery] of [['data-gallery-main', this.#main], ['data-gallery-thumbs', this.#thumbs]]) {
      const source = this.querySelector(`[${templateName}]`);
      const wrapper = gallery?.querySelector('.swiper-wrapper');
      if (!source || !wrapper) continue;
      const slides = [...source.content.children].filter(slide => ['all', this.#flavor].includes(normalize(slide.dataset.flavor))).map(slide => slide.cloneNode(true));
      if (this.#optimized) {
        for (const [index, slide] of slides.entries()) {
          const image = slide.querySelector('img');
          if (image) {
            const isHero = gallery === this.#main && index === 0;
            image.loading = isHero ? 'eager' : 'lazy';
            image.fetchPriority = isHero ? 'high' : 'auto';
          }
        }
      }
      wrapper.replaceChildren(...slides);
    }
    const firstSlide = this.#main?.querySelector('.swiper-slide');
    if (badge && firstSlide) firstSlide.prepend(badge);
    this.#updateHero();
    for (const gallery of [this.#main, this.#thumbs]) {
      const slides = gallery?.querySelectorAll('.swiper-slide') || [];
      for (const [index, slide] of [...slides].entries()) {
        const isHero = gallery === this.#main && index === 0;
        this.#activateImage(slide.querySelector('img'), isHero ? 'high' : 'auto', isHero ? 'eager' : 'lazy');
      }
    }
    this.#observeThumbnails();
  }

  #updateHero() {
    const hero = this.#config.hero?.[this.#flavor]?.subs?.[packNumber(this.#pack) - 1];
    if (!hero) return;
    for (const [gallery, candidates] of [[this.#main, [320, 400, 480, 640, 750, 870, 960, 1200, 1440, 1800, 2000]], [this.#thumbs, [64, 96, 128, 160, 200, 256, 320, 400]]]) {
      const slide = gallery?.querySelector('.swiper-slide');
      const image = slide?.querySelector('img');
      if (!image || slide.hasAttribute('data-video-slide')) continue;
      const source = new URL(hero.src, location.href);
      const urlAtWidth = width => {
        const url = new URL(source);
        url.searchParams.set('width', String(width));
        return url.href;
      };
      const maxWidth = Math.min(hero.width || candidates.at(-1), candidates.at(-1));
      const widths = [...candidates.filter(width => width < maxWidth), maxWidth];
      slide.dataset.src = hero.src;
      image.dataset.src = hero.width ? urlAtWidth(maxWidth) : hero.src;
      image.dataset.srcset = hero.width ? widths.map(width => `${urlAtWidth(width)} ${width}w`).join(', ') : hero.src;
      image.loading = this.#optimized && gallery === this.#thumbs ? 'lazy' : 'eager';
      image.fetchPriority = gallery === this.#main ? 'high' : 'auto';
      if (!this.#optimized || image.srcset !== image.dataset.srcset) image.srcset = image.dataset.srcset;
      if (!this.#optimized || image.src !== new URL(image.dataset.src, location.href).href) image.src = image.dataset.src;
    }
    this.#updateGalleryFocus();
    if (!this.#optimized || this.#mainSwiper?.activeIndex !== 0) this.#mainSwiper?.slideTo(0, 0);
    if (!this.#optimized || this.#thumbSwiper?.activeIndex !== 0) this.#thumbSwiper?.slideTo(0, 0);
  }

  #initSwipers(signal) {
    const init = () => {
      if (signal.aborted || !this.isConnected || this.#mainSwiper || typeof window.Swiper !== 'function') return;
      this.#thumbSwiper = new window.Swiper(this.#thumbs, {
        slidesPerView: 5, spaceBetween: 10,
        breakpoints: { 650: { spaceBetween: 30 } },
      });
      this.#mainSwiper = new window.Swiper(this.#main, {
        slidesPerView: 1, watchSlidesProgress: true,
        thumbs: { swiper: this.#thumbSwiper },
        on: { slideChange: () => {
          const index = this.#mainSwiper.activeIndex;
          this.#loadGalleryWindow(index);
          this.#updateGalleryFocus(index);
          for (const video of this.#main.querySelectorAll('video')) video.pause();
          for (const frame of this.#main.querySelectorAll('[data-video-slide] iframe')) frame.remove();
          const active = this.#main.querySelector('.swiper-wrapper').children[index];
          if (active?.hasAttribute('data-video-slide')) this.#activateVideo(active);
        } },
      });
    };
    init();
    if (typeof window.Swiper !== 'function') document.getElementById('theme-swiper')?.addEventListener('load', init, { once: true, signal });
  }

  #updateGalleryFocus(index = 0) {
    for (const [position, slide] of [...(this.#main?.querySelector('.swiper-wrapper')?.children || [])].entries()) {
      const button = slide.querySelector('.mono-clean-zoom, [data-play-video]');
      if (button) button.tabIndex = position === index ? 0 : -1;
    }
  }

  #activateVideo(slide, play = false) {
    if (!slide) return;
    let video = slide.querySelector('video, iframe');
    if (!video) {
      slide.append(slide.querySelector('[data-video-content]').content.cloneNode(true));
      video = slide.querySelector('video, iframe');
    }
    const button = slide.querySelector('[data-play-video]');
    if (button) button.hidden = true;
    if (video instanceof HTMLVideoElement) {
      if (play) video.play().catch(() => { video.controls = true; });
      else video.preload = 'metadata';
    }
  }

  #openZoom(slide) {
    if (typeof window.lightGallery !== 'function') return;
    const slides = [...this.#main.querySelectorAll('.swiper-slide:not([data-video-slide])')];
    this.#zoom?.destroy();
    this.#zoom = window.lightGallery(this.#main, {
      dynamic: true, thumbnail: true, download: false,
      dynamicEl: slides.map(item => ({ src: item.dataset.src, thumb: item.querySelector('img')?.dataset.src })),
    });
    this.#zoom.openGallery(slides.indexOf(slide));
  }

  #initReviews(signal) {
    const element = this.querySelector('.product-info-reviews');
    if (!element) return;
    const init = () => {
      if (signal.aborted || this.#reviewSwiper || typeof window.Swiper !== 'function') return;
      const create = () => this.#reviewSwiper = new window.Swiper(element, {
        slidesPerView: 2.1, spaceBetween: 10, speed: 1000, loop: true,
        autoplay: matchMedia('(prefers-reduced-motion: reduce)').matches ? false : { delay: 3500, disableOnInteraction: false },
        breakpoints: { 320: { slidesPerView: 1.09 }, 650: { slidesPerView: 2.2 } },
      });
      if (this.#optimized && window.gummiesPerformance) window.gummiesPerformance.whenNear(element, create, { signal });
      else create();
    };
    init();
    if (typeof window.Swiper !== 'function') document.getElementById('theme-swiper')?.addEventListener('load', init, { once: true, signal });
  }

  #refreshFaqHeights() {
    if (this.#optimized) {
      const measurements = this.#faqRows.filter(row => row.content).map(({ item, content }) => ({
        content, height: item.classList.contains('faq-item--open') ? `${content.scrollHeight}px` : '0px',
      }));
      for (const { content, height } of measurements) {
        if (content.style.maxHeight !== height) content.style.maxHeight = height;
      }
      return;
    }
    for (const item of this.querySelectorAll('.mono-clean-faq-item')) {
      const content = item.querySelector('.faq-item__content');
      if (content) content.style.maxHeight = item.classList.contains('faq-item--open') ? `${content.scrollHeight}px` : '0px';
    }
  }

  #closeModal() {
    this.#guideController?.abort();
    this.#guideController = null;
    const modal = this.querySelector('.guide-popup');
    if (!modal || modal.style.display !== 'flex') return;
    modal.style.display = 'none';
    document.body.classList.remove('modal-open');
    if (this.#modalTrigger?.isConnected) this.#modalTrigger.focus();
    this.#modalTrigger = null;
  }

  #scheduleLayout() {
    if (this.#layoutFrame !== undefined) return;
    this.#layoutFrame = requestAnimationFrame(() => {
      this.#layoutFrame = undefined;
      if (!this.isConnected || this.#controller?.signal.aborted) return;
      this.#refreshFaqHeights();
      this.#scaleBadge(this.#galleryWidth);
    });
  }

  async #openGuide(trigger, modal) {
    const stylesheet = this.#optimized ? this.querySelector('[data-mono-guide-styles]') : null;
    if (stylesheet) {
      this.#guideController?.abort();
      const controller = new AbortController();
      this.#guideController = controller;
      this.#controller.signal.addEventListener('abort', () => controller.abort(), { once: true, signal: controller.signal });
      trigger.setAttribute('aria-busy', 'true');
      controller.signal.addEventListener('abort', () => trigger.removeAttribute('aria-busy'), { once: true });
      const ready = await new Promise(resolve => {
        if (stylesheet.dataset.failed) return resolve(false);
        if (stylesheet.dataset.ready || stylesheet.sheet) return resolve(true);
        stylesheet.addEventListener('load', () => resolve(true), { once: true, signal: controller.signal });
        stylesheet.addEventListener('error', () => resolve(false), { once: true, signal: controller.signal });
        controller.signal.addEventListener('abort', () => resolve(false), { once: true });
      });
      if (this.#guideController === controller) trigger.removeAttribute('aria-busy');
      const cancelled = controller.signal.aborted;
      controller.abort();
      if (this.#guideController === controller) this.#guideController = null;
      if (cancelled || !this.isConnected) return;
      if (!ready) {
        location.assign(this.#config.guideUrl);
        return;
      }
      stylesheet.media = 'all';
    }
    this.#modalTrigger = trigger;
    modal.style.display = 'flex';
    document.body.classList.add('modal-open');
    modal.querySelector('.close-popup')?.focus();
  }

  #scaleBadge(width) {
    const badge = this.querySelector('.iframe-frontrow-sticker');
    if (!badge || !this.#main) return;
    const factor = (width || this.#main.offsetWidth) / 870;
    badge.style.setProperty('--frontrow-scale', String(1.5 * factor));
    badge.style.setProperty('--frontrow-translate', `${34 * factor}px`);
  }
}

if (!customElements.get('mono-product-info')) customElements.define('mono-product-info', MonoProductInfo);
